import { Hotel, Users } from 'lucide-react'

import {
    usePendingHairSalonExternalReservations,
    usePendingHairSalonHotelReservations,
} from '@/components/ReservationContext.tsx'
import { useReservationModal } from '@/pages/panel-interno/components/ReservationModalContext'
import { Card, CardContent } from '@/shared/ui/card.tsx'
import type { Reservation } from '@monorepo/functions/src/types/reservations'

import { HairSalonReservationCard } from './ReservationCard'

interface PendingReservationsTabProps {
    onOrganizeCita: (reservation: Reservation) => void
}

export function HairSalonPendingReservationsTab({ onOrganizeCita }: PendingReservationsTabProps) {
    const { pendingReservations: externalReservations, isLoading: isLoadingExternalReservations } =
        usePendingHairSalonExternalReservations()
    const { pendingReservations: hotelReservations, isLoading: isLoadingHotelReservations } =
        usePendingHairSalonHotelReservations()
    const isLoading = isLoadingExternalReservations || isLoadingHotelReservations
    const { openReservationModal } = useReservationModal()

    return (
        <Card>
            <CardContent>
                <div className='mt-6 space-y-8'>
                    {isLoading ? (
                        <div className='py-8 text-center text-muted-foreground'>Cargando solicitudes pendientes...</div>
                    ) : (
                        <>
                            {/* Sección de Reservas de Hotel */}
                            {hotelReservations.length > 0 && (
                                <div>
                                    <div className='mb-4 flex items-center gap-2'>
                                        <Hotel className='h-5 w-5 text-primary' />
                                        <h3 className='text-lg font-semibold'>Reservas de Hotel</h3>
                                    </div>
                                    <div className='scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent overflow-x-auto'>
                                        <div className='grid auto-cols-[calc(33.333%-0.5rem)] grid-flow-col gap-4 pb-2 md:auto-cols-[300px]'>
                                            {hotelReservations.map(reservation => (
                                                <HairSalonReservationCard
                                                    key={reservation.id}
                                                    reservation={reservation}
                                                    onOrganizeCita={onOrganizeCita}
                                                    onShowDetails={openReservationModal}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Sección de Reservas Externas */}
                            {externalReservations.length > 0 && (
                                <div>
                                    <div className='mb-4 flex items-center gap-2'>
                                        <Users className='h-5 w-5 text-primary' />
                                        <h3 className='text-lg font-semibold'>Reservas Externas</h3>
                                    </div>
                                    <div className='scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent overflow-x-auto'>
                                        <div className='grid auto-cols-[calc(33.333%-0.5rem)] grid-flow-col gap-4 pb-2 md:auto-cols-[300px]'>
                                            {externalReservations.map(reservation => (
                                                <HairSalonReservationCard
                                                    key={reservation.id}
                                                    reservation={reservation}
                                                    onOrganizeCita={onOrganizeCita}
                                                    onShowDetails={openReservationModal}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Mensaje cuando no hay reservas */}
                            {hotelReservations.length === 0 && externalReservations.length === 0 && (
                                <div className='py-8 text-center'>
                                    <p className='text-muted-foreground'>No hay reservas pendientes</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}
